import {
  Button,
  CircularProgress,
  Modal,
  TextField,
  Typography,
  Box,
  Snackbar,
  SnackbarContent,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { createNewUser, deleteUser, getUsers } from "src/services/users";
import * as Yup from "yup";
import {
  ButtonContainer,
  InputContainer,
  ModalContainer,
  UserContainter,
} from "./styles";
import UserList from "../UserList";

interface IUserModal {
  name: string;
  cpf: string;
  id: number;
}

interface INewUserModal {
  open: boolean;
  handleClose: () => void;
}

const validateCPF = (cpf: string) => {
  cpf = cpf.replace(/[^\d]+/g, "");
  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(cpf.charAt(i)) * (10 - i);
  let rest = (sum * 10) % 11;
  if (rest === 10 || rest === 11) rest = 0;
  if (rest !== parseInt(cpf.charAt(9))) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(cpf.charAt(i)) * (11 - i);
  rest = (sum * 10) % 11;
  if (rest === 10 || rest === 11) rest = 0;

  return rest === parseInt(cpf.charAt(10));
};

const validationSchema = Yup.object({
  name: Yup.string()
    .required("Nome é obrigatório")
    .test("nome-sobrenome", "Informe nome e sobrenome", (value) => {
      if (!value) return false;
      return value.trim().split(" ").length >= 2;
    }),
  cpf: Yup.string()
    .required("CPF é obrigatório")
    .test("cpf-valido", "CPF inválido", (value) => !!value && validateCPF(value)),
  password: Yup.string()
    .required("Senha é obrigatória")
    .min(8, "A senha deve ter pelo menos 8 caracteres"),
  passwordConfirmation: Yup.string()
    .required("Confirmação de senha é obrigatória")
    .oneOf([Yup.ref("password")], "As senhas devem coincidir"),
});

export default function NewUserModal({ open, handleClose }: INewUserModal) {
  const [users, setUsers] = useState<IUserModal[] | null>(null);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const [creatingNewUser, setCreatingNewUser] = useState<boolean>(false);
  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);
  const [toast, setToast] = useState<any>(null);

  const listUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await getUsers();
      setUsers(response.data);
    } catch (error) {
      setToast({ message: "Erro ao carregar usuários", type: "error" });
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSubmit = async (values: any, resetForm: () => void) => {
    try {
      setCreatingNewUser(true);

      const payload = {
        name: values.name,
        cpf: values.cpf,
        password: values.password,
        password_confirmation: values.passwordConfirmation,
      };

      const response = await createNewUser(payload);

      if (response?.status === 201) {
        setToast({ message: "Usuário criado com sucesso! ✅", type: "success" });
        resetForm();
        listUsers();
      }
    } catch (error) {
      setToast({
        message: "Ocorreu um erro ao salvar novo usuário",
        type: "error",
      });
    } finally {
      setCreatingNewUser(false);
    }
  };

  const handleDelete = async (id: number) => {
    const confirmDelete = window.confirm(
      "Tem certeza que deseja excluir este usuário?"
    );
    if (!confirmDelete) return;

    try {
      setDeletingUserId(id);
      await deleteUser(id);
      setToast({ message: "Usuário excluído com sucesso! ✅", type: "success" });
    } catch (error) {
      setToast({ message: "Erro ao deletar usuário ❌", type: "error", });
    } finally {
      setDeletingUserId(null);
      listUsers();
    }
  };

  useEffect(() => {
    listUsers();
  }, []);

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <ModalContainer>
          <UserContainter>
            <Typography id="modal-title" component="h2" gutterBottom>
              Novo usuário
            </Typography>

            <Formik
              initialValues={{
                name: "",
                cpf: "",
                password: "",
                passwordConfirmation: "",
              }}
              validationSchema={validationSchema}
              onSubmit={(values, { resetForm }) => handleSubmit(values, resetForm)}
            >
              {({ values, errors, touched, handleChange, handleBlur }) => (
                <Form>
                  <InputContainer>
                    <TextField
                      fullWidth
                      required
                      label="Nome completo"
                      size="small"
                      margin="normal"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                    />
                    <TextField
                      fullWidth
                      required
                      label="CPF"
                      size="small"
                      margin="normal"
                      name="cpf"
                      value={values.cpf}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.cpf && Boolean(errors.cpf)}
                      helperText={touched.cpf && errors.cpf}
                    />
                  </InputContainer>

                  <InputContainer>
                    <TextField
                      fullWidth
                      required
                      label="Senha"
                      type="password"
                      size="small"
                      margin="normal"
                      name="password"
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                    />
                    <TextField
                      fullWidth
                      required
                      label="Confirmar Senha"
                      type="password"
                      size="small"
                      margin="normal"
                      name="passwordConfirmation"
                      value={values.passwordConfirmation}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.passwordConfirmation &&
                        Boolean(errors.passwordConfirmation)
                      }
                      helperText={
                        touched.passwordConfirmation &&
                        errors.passwordConfirmation
                      }
                    />
                  </InputContainer>

                  {loadingUsers ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        py: 4,
                      }}
                    >
                      <CircularProgress color="primary" />
                    </Box>
                  ) : (
                    <UserList
                      users={users}
                      onDelete={handleDelete}
                      deletingUserId={deletingUserId}
                    />
                  )}

                  <ButtonContainer>
                    <Button
                      variant="outlined"
                      onClick={handleClose}
                      disabled={creatingNewUser}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={creatingNewUser}
                    >
                      {creatingNewUser ? (
                        <CircularProgress size={22} color="inherit" />
                      ) : (
                        "Salvar"
                      )}
                    </Button>
                  </ButtonContainer>
                </Form>
              )}
            </Formik>
          </UserContainter>
        </ModalContainer>
      </Modal>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <SnackbarContent
          message={toast?.message}
          sx={{
            backgroundColor: "#fff",
            color: toast?.type === "success" ? "#4caf50" : "#f44336",
            fontWeight: 700,
            borderRadius: 1,
          }}
        />
      </Snackbar>
    </>
  );
}
